// welcome-step.tsx
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../../../contexts/theme-context';
import { OnboardingStepProps } from '../../registration';
import {
  ContentSection,
  Heading,
  ContinueButton,
} from '../secure-your-account/secure-your-account.styled';
import { InputsContainer } from './confirm-your-profile.styled';
import { FloatingInput } from '../../../../components/floating-input/floating-input';
import { FaSpinner } from 'react-icons/fa';
import { VSpace } from '../../../chat/settings/account/account-settings';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { RegistrationData } from '../../../../contexts/auth/types';

export const ConfirmYourProfile: React.FC<OnboardingStepProps> = ({ goNext }) => {
  const { isLight } = useTheme();
  const { setRegistration, loading, onboarding } = useAuth();

  // 🔽 initialize from onboarding (this is your profile data source)
  const [firstName, setFirstName] = useState(onboarding?.firstName ?? '');
  const [lastName, setLastName] = useState(onboarding?.lastName ?? '');
  const [nickname, setNickname] = useState(onboarding?.nickname ?? '');
  const [company, setCompany] = useState(onboarding?.company ?? '');

  // ✅ keep local state in sync if onboarding data arrives later (e.g. profile load)
  useEffect(() => {
    setFirstName(onboarding?.firstName ?? '');
    setLastName(onboarding?.lastName ?? '');
    setNickname(onboarding?.nickname ?? '');
    setCompany(onboarding?.company ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboarding?.firstName, onboarding?.lastName, onboarding?.nickname, onboarding?.company]);

  const normalize = (s: string) => s.trim();

  // Require: First Name, Last Name, Company
  const allValid = useMemo(() => {
    return normalize(firstName) !== '' && normalize(lastName) !== '' && normalize(company) !== '';
  }, [firstName, lastName, company]);

  const handleContinue = () => {
    const firstNameClean = normalize(firstName);
    const lastNameClean = normalize(lastName);
    const nicknameClean = normalize(nickname);
    const companyClean = normalize(company);

    const fullName = [firstNameClean, lastNameClean].filter(Boolean).join(' ');

    const patch: Partial<RegistrationData> = {
      firstName: firstNameClean,
      lastName: lastNameClean,
      fullName,
      company: companyClean,
    };

    // 👇 only set nickname if there's a non-empty value
    if (nicknameClean) {
      patch.nickname = nicknameClean;
    }

    setRegistration(patch);
    goNext();
  };

  return (
    <ContentSection data-testid="ob-welcome">
      <div>
        <VSpace $h={32} />
        <Heading isLight={isLight} data-testid="ob-welcome-heading">
          Confirm your profile.
        </Heading>
      </div>
      <div style={{ width: '100%' }}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}
          data-testid="ob-welcome-form"
        >
          <InputsContainer data-testid="ob-welcome-inputs">
            <FloatingInput
              editText
              id="first-name"
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              maxLength={100}
              autoComplete="given-name"
              data-testid="ob-welcome-first-name"
            />

            <FloatingInput
              editText
              id="last-name"
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              maxLength={100}
              autoComplete="family-name"
              data-testid="ob-welcome-last-name"
            />

            <FloatingInput
              editText
              id="nickname"
              label="Nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={100}
              autoComplete="nickname"
              data-testid="ob-welcome-nickname"
            />

            <FloatingInput
              editText
              id="company"
              label="Company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              maxLength={100}
              autoComplete="organization"
              data-testid="ob-welcome-company"
            />
          </InputsContainer>
        </div>
      </div>
      <ContinueButton
        disabled={!allValid}
        onClick={handleContinue}
        aria-busy={loading}
        data-testid="ob-welcome-continue"
      >
        {loading ? <FaSpinner aria-label="Loading" data-testid="ob-welcome-spinner" /> : 'Continue'}
      </ContinueButton>
    </ContentSection>
  );
};

(ConfirmYourProfile as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
(ConfirmYourProfile as any).progress = 'Setup';
(ConfirmYourProfile as any).smallLogo = true;
(ConfirmYourProfile as any).fill = false;
